import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AuthService } from '@core/services/auth.service';
import { ApiService } from '@core/services/api.service';
import { SnackbarService } from '@core/services/snackbar.service';
import { SidebarService } from '@core/services/sidebar.service';

@Component({
  standalone: false,
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);

  email: string = '';
  password: string = '';
  rememberMe: boolean = false;
  loading: boolean = false;
  hidePassword: boolean = true;
  submitted: boolean = false;
  loginError: string = '';

  currentView: string = 'login';
  readonly year = new Date().getFullYear();
  fpEmail: string = '';
  fpOtp: string = '';
  fpNewPassword: string = '';
  fpConfirmPassword: string = '';
  fpResetToken: string = '';
  fpLoading: boolean = false;
  fpError: string = '';
  fpSuccess: string = '';
  fpSubmitted: boolean = false;
  hideNewPw: boolean = true;
  hideConfirmPw: boolean = true;
  otpResendCountdown: number = 0;
  private countdownTimer: any;

  fcNewPassword: string = '';
  fcConfirmPassword: string = '';
  fcLoading: boolean = false;
  fcError: string = '';
  fcSubmitted: boolean = false;
  hideFcNew: boolean = true;
  hideFcConfirm: boolean = true;
  private pendingRedirect: string = '/overview';

  caName: string = '';
  caEmail: string = '';
  caMessage: string = '';
  caLoading: boolean = false;
  caError: string = '';
  caSuccess: boolean = false;
  caSubmitted: boolean = false;


  constructor(
    private authService: AuthService,
    private apiService: ApiService,
    private router: Router,
    private snackbarService: SnackbarService,
    private sidebarService: SidebarService
  ) {}

  ngOnInit(): void {
    this.sidebarService.setVisible(false);
    if (this.authService.getToken() && this.authService.mustChangePassword()) {
      this.currentView = 'force-change';
      return;
    }
    const savedEmail = localStorage.getItem('hrms_remember_email');
    if (savedEmail) {
      this.email = savedEmail;
      this.rememberMe = true;
    }
    localStorage.removeItem('hrms_remember');
  }

  ngOnDestroy(): void {
    if (this.countdownTimer) clearInterval(this.countdownTimer);
  }

  fillDemo(account: any): void {
    this.email = account.email;
    this.password = account.password;
    this.submitted = false;
    this.loginError = '';
  }

  get emailValid(): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.email);
  }

  get passwordValid(): boolean {
    return this.password.length >= 4;
  }


  private isEmployeeRoute(url: string): boolean {
    const allowed = ['/overview', '/dashboard', '/attendance', '/leaves', '/holidays',
      '/announcements', '/profile', '/performance', '/my-payslips', '/overtime'];
    return allowed.some(r => url === r || url.startsWith(r + '/') || url.startsWith(r + '?'));
  }

  openContactAdmin(): void {
    this.currentView = 'contact';
    this.caName = '';
    this.caEmail = this.email || '';
    this.caMessage = '';
    this.caError = '';
    this.caSuccess = false;
    this.caSubmitted = false;
  }

  submitContactAdmin(): void {
    this.caSubmitted = true;
    this.caError = '';
    if (!this.caName.trim()) { this.caError = 'Please enter your name'; return; }
    if (!this.caEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.caEmail)) { this.caError = 'Please enter a valid email'; return; }
    if (!this.caMessage.trim() || this.caMessage.trim().length < 10) { this.caError = 'Please describe your issue (min 10 characters)'; return; }
    this.caLoading = true;
    this.apiService.post('/auth/contact-admin', { name: this.caName.trim(), email: this.caEmail.trim(), message: this.caMessage.trim() }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => { this.caLoading = false; this.caSuccess = true; },
      error: (err: any) => {
        this.caLoading = false;
        this.caError = err?.error?.message || 'Failed to send message. Please try again.';
      }
    });
  }

  openForgotPassword(): void {
    this.currentView = 'forgot';
    this.fpEmail = this.email;
    this.fpError = '';
    this.fpSuccess = '';
    this.fpSubmitted = false;
  }

  backToLogin(): void {
    this.currentView = 'login';
    this.fpEmail = '';
    this.fpOtp = '';
    this.fpNewPassword = '';
    this.fpConfirmPassword = '';
    this.fpResetToken = '';
    this.fpError = '';
    this.fpSuccess = '';
    this.fpSubmitted = false;
    if (this.countdownTimer) clearInterval(this.countdownTimer);
    this.otpResendCountdown = 0;
  }

  sendOtp(): void {
    this.fpSubmitted = true;
    this.fpError = '';
    if (!this.fpEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.fpEmail)) {
      this.fpError = 'Please enter a valid email address';
      return;
    }
    this.fpLoading = true;
    this.authService.forgotPassword(this.fpEmail).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.fpLoading = false;
        this.fpSubmitted = false;
        this.currentView = 'otp';
        this.startResendCountdown();
      },
      error: (err: any) => {
        this.fpLoading = false;
        this.fpError = err.error?.message || 'No account found with this email';
      }
    });
  }

  resendOtp(): void {
    if (this.otpResendCountdown > 0) return;
    this.fpError = '';
    this.fpLoading = true;
    this.authService.forgotPassword(this.fpEmail).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.fpLoading = false;
        this.fpSuccess = 'OTP resent successfully';
        this.startResendCountdown();
        setTimeout(() => { this.fpSuccess = ''; }, 3000);
      },
      error: (err: any) => {
        this.fpLoading = false;
        this.fpError = err.error?.message || 'Failed to resend OTP';
      }
    });
  }

  private startResendCountdown(): void {
    this.otpResendCountdown = 60;
    if (this.countdownTimer) clearInterval(this.countdownTimer);
    this.countdownTimer = setInterval(() => {
      this.otpResendCountdown--;
      if (this.otpResendCountdown <= 0) clearInterval(this.countdownTimer);
    }, 1000);
  }

  verifyOtp(): void {
    this.fpSubmitted = true;
    this.fpError = '';
    if (!this.fpOtp || this.fpOtp.length < 4) {
      this.fpError = 'Please enter the OTP sent to your email';
      return;
    }
    this.fpLoading = true;
    this.authService.verifyOtp(this.fpEmail, this.fpOtp).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.fpLoading = false;
        this.fpSubmitted = false;
        this.fpResetToken = res?.data?.resetToken || res?.resetToken || '';
        this.currentView = 'reset';
      },
      error: (err: any) => {
        this.fpLoading = false;
        this.fpError = err.error?.message || 'Invalid or expired OTP';
      }
    });
  }

  resetPassword(): void {
    this.fpSubmitted = true;
    this.fpError = '';
    if (!this.fpNewPassword || this.fpNewPassword.length < 6) {
      this.fpError = 'Password must be at least 6 characters';
      return;
    }
    if (this.fpNewPassword !== this.fpConfirmPassword) {
      this.fpError = 'Passwords do not match';
      return;
    }
    this.fpLoading = true;
    this.authService.resetPassword(this.fpResetToken, this.fpNewPassword).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.fpLoading = false;
        this.currentView = 'login';
        this.fpEmail = '';
        this.fpOtp = '';
        this.fpNewPassword = '';
        this.fpConfirmPassword = '';
        this.fpResetToken = '';
        this.loginError = '';
        this.snackbarService.showSuccess('Password reset successfully. Please sign in.');
      },
      error: (err: any) => {
        this.fpLoading = false;
        this.fpError = err.error?.message || 'Failed to reset password. Please try again.';
      }
    });
  }

  login(): void {
    this.submitted = true;
    this.loginError = '';
    if (!this.email || !this.password) {
      return;
    }
    if (!this.emailValid) {
      return;
    }
    this.loading = true;
    this.authService.login({ email: this.email, password: this.password }, this.rememberMe).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response: any) => {
        this.loading = false;
        if (this.rememberMe) {
          localStorage.setItem('hrms_remember_email', this.email);
        } else {
          localStorage.removeItem('hrms_remember_email');
        }
        const savedRedirect = sessionStorage.getItem('redirect_url');
        sessionStorage.removeItem('redirect_url');
        const role = (response?.data?.role || '').toLowerCase().replace(/^role_/, '');
        const defaultRoute = role === 'manager' ? '/dashboard/manager' : '/overview';
        const canUseRedirect = savedRedirect && (
          role === 'admin' || role === 'super_admin' || role === 'hr' || role === 'hr_admin' ||
          this.isEmployeeRoute(savedRedirect)
        );
        this.pendingRedirect = (canUseRedirect ? savedRedirect : null) || defaultRoute;

        if (this.authService.mustChangePassword()) {
          this.currentView = 'force-change';
          this.fcNewPassword = '';
          this.fcConfirmPassword = '';
          this.fcError = '';
          this.fcSubmitted = false;
        } else {
          this.snackbarService.showSuccess('Login successful');
          this.router.navigate([this.pendingRedirect]).then((navigated) => {
            if (navigated) this.sidebarService.setVisible(true);
          });
        }
      },
      error: (error: any) => {
        this.loading = false;
        this.loginError = error.error?.message || 'Invalid email or password';
      }
    });
  }

  forceChangePassword(): void {
    this.fcSubmitted = true;
    this.fcError = '';
    if (!this.fcNewPassword || this.fcNewPassword.length < 8) {
      this.fcError = 'Password must be at least 8 characters';
      return;
    }
    if (this.fcNewPassword !== this.fcConfirmPassword) {
      this.fcError = 'Passwords do not match';
      return;
    }
    this.fcLoading = true;
    this.apiService.post('/auth/force-change-password', {
      newPassword: this.fcNewPassword
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.fcLoading = false;
        this.authService.clearMustChangePassword();
        this.snackbarService.showSuccess('Password updated. Welcome!');
        this.router.navigate([this.pendingRedirect]).then((navigated) => {
          if (navigated) this.sidebarService.setVisible(true);
        });
      },
      error: (err: any) => {
        this.fcLoading = false;
        this.fcError = err.error?.message || 'Failed to update password. Please try again.';
      }
    });
  }
}
